/**
 * How wide each column of Home's list is at a given container width.
 *
 * A width someone dragged is a preference, not a promise. When the window
 * shrinks the table first gives up the slack inside the name column, then
 * narrows the metadata columns down to readable floors. Only when even the
 * floors no longer fit does the table overflow and the list gain its horizontal
 * scrollbar — so a scrollbar never appears while there is still space to take.
 */
export type NodeListColumnId = 'name' | 'type' | 'owner' | 'date';

/** Mirrors `NodeList`'s persisted per-browser widths. */
export interface NodeListColumnWidths {
  /** Absent until dragged: the name column takes whatever the rest leaves. */
  name?: number;
  type: number;
  owner: number;
  date: number;
}

export interface NodeListColumnLayout {
  /** Real columns in render order; the actions track is added to the template. */
  widths: readonly { id: NodeListColumnId; width: number }[];
  /** CSS `grid-template-columns`, every track a whole number of pixels. */
  template: string;
  /** Columns, gaps, row padding and the actions track: the full table width. */
  totalWidth: number;
  /** True only when the floors do not fit, which is when the wrapper scrolls. */
  overflows: boolean;
}

/** Keep in sync with the header/row markup: `gap-3` and `px-3`. */
export const NODE_LIST_GAP = 12;
export const NODE_LIST_ROW_PADDING = 24;
/** The star and menu buttons; fixed so they never disappear behind a scrollbar. */
export const NODE_LIST_ACTIONS_WIDTH = 80;

/** Readable floors: narrower than this and the column stops being a column. */
export const NODE_LIST_COLUMN_MIN: Record<NodeListColumnId, number> = {
  name: 140,
  type: 64,
  owner: 88,
  date: 96,
};

/** What the name column asks for before the window hands it the slack. */
export const NODE_LIST_DEFAULT_NAME_WIDTH = 320;

/** A drag may go below a column's own floor, but not below this. */
export const NODE_LIST_DRAG_MIN = 60;

export function layoutNodeListColumns(
  containerWidth: number,
  widths: NodeListColumnWidths,
  showOwner: boolean,
): NodeListColumnLayout {
  const ids: NodeListColumnId[] = showOwner
    ? ['name', 'type', 'owner', 'date']
    : ['name', 'type', 'date'];

  const preferred: Record<NodeListColumnId, number> = {
    name: widths.name ?? NODE_LIST_DEFAULT_NAME_WIDTH,
    type: widths.type,
    owner: widths.owner,
    date: widths.date,
  };
  const floor: Record<NodeListColumnId, number> = {
    name: Math.min(preferred.name, NODE_LIST_COLUMN_MIN.name),
    type: Math.min(preferred.type, NODE_LIST_COLUMN_MIN.type),
    owner: Math.min(preferred.owner, NODE_LIST_COLUMN_MIN.owner),
    date: Math.min(preferred.date, NODE_LIST_COLUMN_MIN.date),
  };

  const safeWidth = Number.isFinite(containerWidth)
    ? Math.max(0, Math.floor(containerWidth))
    : 0;
  // One gap fewer than the number of tracks (the last track is the actions).
  const chrome = NODE_LIST_ROW_PADDING + NODE_LIST_ACTIONS_WIDTH + NODE_LIST_GAP * ids.length;
  const available = Math.max(0, safeWidth - chrome);

  const others = ids.filter((id) => id !== 'name');
  const othersPreferred = total(others, preferred);
  // Until someone drags it the name column fills the leftover width, which is
  // what keeps a wide Home from leaving a dead strip on the right. Once it has
  // been dragged its width is the person's choice, and dragging it narrower
  // really narrows it — the space it gives up stays empty at the right.
  if (widths.name === undefined) {
    preferred.name = Math.max(floor.name, available - othersPreferred);
  }

  const preferredTotal = total(ids, preferred);
  const floorTotal = total(ids, floor);

  const actual: Record<NodeListColumnId, number> = { ...floor };

  if (available >= preferredTotal) {
    for (const id of ids) actual[id] = preferred[id];
  } else if (available > floorTotal) {
    // Shrink the name column first: its text truncates gracefully, while a
    // squeezed date reads as broken. The metadata only gives up width once
    // the name column has nothing left to give.
    const nameCut = Math.min(preferred.name - floor.name, preferredTotal - available);
    actual.name = preferred.name - nameCut;
    const forOthers = available - actual.name;
    if (forOthers >= othersPreferred) {
      for (const id of others) actual[id] = preferred[id];
      actual.name = available - othersPreferred;
    } else {
      const allocated = allocate(forOthers, others, floor, preferred);
      for (const id of others) actual[id] = allocated[id];
    }
  }
  // Otherwise every column sits on its floor and the table overflows.

  const columns = ids.map((id) => ({ id, width: actual[id] }));
  const totalWidth = chrome + columns.reduce((sum, column) => sum + column.width, 0);

  return {
    widths: columns,
    template: `${columns.map((column) => `${column.width}px`).join(' ')} ${NODE_LIST_ACTIONS_WIDTH}px`,
    totalWidth,
    overflows: totalWidth > safeWidth,
  };
}

function total(
  ids: readonly NodeListColumnId[],
  values: Record<NodeListColumnId, number>,
): number {
  return ids.reduce((sum, id) => sum + values[id], 0);
}

/**
 * Splits `width` among `ids` between their floors and preferred widths,
 * proportionally to how much each can give up. Whole pixels only, so the grid
 * never overshoots its box by a fraction and grows a permanent scrollbar.
 */
function allocate(
  width: number,
  ids: readonly NodeListColumnId[],
  floor: Record<NodeListColumnId, number>,
  preferred: Record<NodeListColumnId, number>,
): Record<NodeListColumnId, number> {
  const out = {} as Record<NodeListColumnId, number>;
  for (const id of ids) out[id] = floor[id];

  const minimum = total(ids, floor);
  const capacity = ids.reduce((sum, id) => sum + (preferred[id] - floor[id]), 0);
  let remaining = width - minimum;
  if (remaining <= 0 || capacity <= 0) return out;

  if (remaining >= capacity) {
    for (const id of ids) out[id] = preferred[id];
    return out;
  }

  const fractions = ids.map((id) => {
    const room = preferred[id] - floor[id];
    const share = (room * remaining) / capacity;
    const whole = Math.floor(share);
    out[id] = floor[id] + whole;
    return { id, fraction: share - whole };
  });

  let leftover = remaining - (total(ids, out) - minimum);
  for (const { id } of [...fractions].sort((a, b) => b.fraction - a.fraction)) {
    if (leftover <= 0) break;
    if (out[id] < preferred[id]) {
      out[id] += 1;
      leftover -= 1;
    }
  }
  return out;
}
